import { backOff } from 'exponential-backoff';

const DEFAULT_CHUNK_SIZE = 1 * 256 * 1024; // 256 KiB (must be multiple of 256 KiB)
const DEFAULT_MAX_RETRY_DELAY = 30000; // 30 s

type VideoUploadClientOptions = {
  /** Upload chunk size (must be multiple of 256 KiB) */
  chunkSize?: number;

  /** The maximum request retry backoff delay */
  maxRetryDelay?: number;
};

class VideoUploadClient {
  private readonly chunkSize: number;
  private readonly maxRetryDelay: number;
  private readonly chunks: Readonly<Blob>[] = [];
  private byteOffset = 0;
  private uploadTimer: NodeJS.Timeout | null = null;
  private isPaused = false;
  private isFinishing = false;
  private isFinished = false;
  private chunkBuffer: ArrayBuffer;
  private chunkBufferView: Uint8Array;
  private nChunkBufferBytes = 0;
  private finishedPromise: Promise<void>;
  private resolveFinished: () => void;

  constructor(
    private readonly uploadUrl: string,
    {
      chunkSize = DEFAULT_CHUNK_SIZE,
      maxRetryDelay = DEFAULT_MAX_RETRY_DELAY,
    }: VideoUploadClientOptions = {
      chunkSize: DEFAULT_CHUNK_SIZE,
      maxRetryDelay: DEFAULT_MAX_RETRY_DELAY,
    },
  ) {
    this.chunkSize = chunkSize;
    this.maxRetryDelay = maxRetryDelay;
    this.chunkBuffer = new ArrayBuffer(this.chunkSize);
    this.chunkBufferView = new Uint8Array(this.chunkBuffer);
    // Start the async upload loop. It won't do anything until chunks are
    // queued and isUploading is set to true.
    this.uploadAsync();

    this.resolveFinished = () => {};
    this.finishedPromise = new Promise<void>((resolve) => {
      this.resolveFinished = resolve;
    }).then(() => {
      this.isFinished = true;
    });
  }

  private async fillChunkBuffer(): Promise<void> {
    while (this.chunks.length > 0 && this.nChunkBufferBytes < this.chunkSize) {
      const chunk = this.chunks.shift();
      if (!chunk) {
        break;
      }
      const arrayBuffer = await chunk.arrayBuffer();
      const nBytes = Math.min(
        arrayBuffer.byteLength,
        this.chunkSize - this.nChunkBufferBytes,
      );
      const bytes = new Uint8Array(arrayBuffer, 0, nBytes);
      this.chunkBufferView.set(bytes, this.nChunkBufferBytes);
      this.nChunkBufferBytes += nBytes;

      const remainingBytes = new Uint8Array(arrayBuffer, nBytes);
      if (remainingBytes.length > 0) {
        this.chunks.unshift(new Blob([remainingBytes]));
      }
    }
  }

  private async uploadChunk(
    chunk: Readonly<ArrayBuffer>,
    byteOffset: number,
    isFinalChunk: boolean,
  ): Promise<void> {
    const totalByteStr = isFinalChunk
      ? `${byteOffset + chunk.byteLength}`
      : '*';
    const byteRangeStr =
      chunk.byteLength === 0
        ? `bytes */${totalByteStr}`
        : `bytes ${byteOffset}-${byteOffset + chunk.byteLength - 1}/${totalByteStr}`;

    const response = await fetch(this.uploadUrl, {
      method: 'PUT',
      body: chunk,
      headers: {
        'Content-Range': byteRangeStr,
      },
    });

    // TODO: check the response for the actual range that was uploaded (it may
    // be less than the full chunk size).
    console.log('Uploaded chunk:', response);

    if (!response.ok && (isFinalChunk || response.status !== 308)) {
      // 308 Resume Incomplete is expected for non-final chunks
      throw new Error('Failed to upload chunk');
    }
  }

  private uploadChunkWithRetry(
    chunk: Readonly<ArrayBuffer>,
    byteOffset: number,
    isFinalChunk: boolean,
  ): Promise<void> {
    return backOff(() => this.uploadChunk(chunk, byteOffset, isFinalChunk), {
      maxDelay: this.maxRetryDelay,
      numOfAttempts: Infinity,
      retry: (err) => {
        // TODO: we probably don't always want to retry
        console.warn('Retrying upload:', err);
        return true;
      },
    });
  }

  private async uploadAsync(): Promise<void> {
    // Only process chunks when not paused
    while (!this.isPaused && this.chunks.length > 0) {
      await this.fillChunkBuffer();

      // If we don't have a full buffer, we're done for now
      if (this.nChunkBufferBytes < this.chunkSize) {
        break;
      }

      try {
        await this.uploadChunkWithRetry(
          this.chunkBuffer,
          this.byteOffset,
          false,
        );
        this.nChunkBufferBytes = 0;
        this.byteOffset += this.chunkBuffer.byteLength;
      } catch (err) {
        console.error('Failed to upload chunk:', err);
        // Fatal error
        return;
      }
    }

    if (this.isFinishing) {
      // Upload whatever is left in the chunk buffer
      try {
        const finalChunk = this.chunkBuffer.slice(0, this.nChunkBufferBytes);
        await this.uploadChunkWithRetry(finalChunk, this.byteOffset, true);
        this.resolveFinished();
        return; // We're done
      } catch (err) {
        console.error('Failed to upload final chunk:', err);
        // Fatal error
      }
    }

    // We're not done - call this method again
    this.uploadTimer = setTimeout(() => this.uploadAsync(), 1000);
  }

  queueChunk(chunk: Readonly<Blob>): void {
    if (this.isFinishing) {
      return;
    }
    this.chunks.push(chunk);
  }

  resume(): void {
    if (this.isFinishing) {
      return;
    }
    this.isPaused = false;
  }

  pause(): void {
    if (this.isFinishing) {
      return;
    }
    this.isPaused = true;
  }

  async finish(): Promise<void> {
    if (this.isFinished) {
      return;
    }

    this.isPaused = false;
    this.isFinishing = true;
    await this.finishedPromise;
  }

  destroy(): void {
    this.pause();

    if (this.uploadTimer) {
      clearTimeout(this.uploadTimer);
      this.uploadTimer = null;
    }
  }
}

export default VideoUploadClient;
